/** @jsxImportSource @emotion/react */
import React from 'react'
import { SerializedStyles } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

interface CanlanTextLogoProps {
  height?: number
  color?: string
  style?: SerializedStyles
}

/**
 * Canlan Stats text logo component
 * Maintains aspect ratio of 541.993:65.671 (width:height)
 */
const CanlanTextLogo: React.FC<CanlanTextLogoProps> = ({
  height = 24,
  color = colors.WHITE,
  style,
}) => {
  // Calculate width to maintain aspect ratio (541.993:65.671)
  const width = (height * 541.993) / 65.671

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={width}
      height={height}
      viewBox='0 0 541.993 65.671'
      fill='none'
      css={style}
    >
      <g transform='translate(-735.186 -547.472)'>
        <g transform='translate(1262.709 557.417)'>
          <path
            d='M1717.251,567.066h-2.119v6.346H1713.9v-6.346h-2.13v-1.183h5.486Z'
            transform='translate(-1711.765 -565.883)'
            fill={color}
          />
          <path
            d='M1732.419,573.412h-1.236v-5.367l-2.291,3.786h-.151l-2.291-3.8v5.378h-1.236v-7.529h1.334l2.269,3.765,2.281-3.765h1.322Z'
            transform='translate(-1717.949 -565.883)'
            fill={color}
          />
        </g>
        <path
          d='M849.294,586.327c0-11.977,8.9-21.221,21.221-21.221a20.463,20.463,0,0,1,17.617,9.651l-8.194,5.265a12.492,12.492,0,0,0-9.422-4.085c-6.57,0-10.872,4.535-10.872,11.047a10.2,10.2,0,0,0,6.82,9.343,11.8,11.8,0,0,0,12.7-3.41l7.768,6.183c-3.547,5.872-9.114,8.229-16.615,8.248A20.726,20.726,0,0,1,849.294,586.327Z'
          transform='translate(-52.47 -8.109)'
          fill={color}
        />
        <path
          d='M1038.374,565.428v42.2h-7.7L1016.9,586.517v21.114h-9.985v-42.2h7.754l13.778,21.332V565.428Z'
          transform='translate(-124.947 -8.257)'
          fill={color}
        />
        <path
          d='M1243.586,565.428v42h-7.7l-13.778-21.114v21.114h-9.985v-42h7.754l13.778,21.132V565.428Z'
          transform='translate(-219.309 -8.257)'
          fill={color}
        />
        <path
          d='M964.052,607.38,951.43,565.657H939.707l-12.622,42.087h10.432s2.8-9.128,2.8-9.126A119.647,119.647,0,0,1,964.052,607.38Zm-14.764-15.061a22.626,22.626,0,0,0-3.572-1.205c-1.224-.367-1.05-.21-3.372-.906l3.224-11.866S949.283,592.3,949.288,592.318Z'
          transform='translate(-88.24 -8.362)'
          fill={color}
        />
        <path
          d='M1169.162,607.38l-12.622-41.723h-11.723l-12.622,42.087h10.432s2.8-9.128,2.8-9.126A119.635,119.635,0,0,1,1169.162,607.38ZM1154.4,592.318a22.649,22.649,0,0,0-3.572-1.205c-1.223-.367-1.05-.21-3.372-.906l3.224-11.866S1154.394,592.3,1154.4,592.318Z'
          transform='translate(-182.555 -8.362)'
          fill={color}
        />
        <path
          d='M1104.345,597.361h-13.22V565.428h-9.985v42.2h20.07Z'
          transform='translate(-159.079 -8.257)'
          fill={color}
        />
        <path
          d='M782.088,591.878a60.55,60.55,0,0,0-10.8-4.28A81.933,81.933,0,0,0,754,584.1q-2.12-.229-4.373-.373a137.51,137.51,0,0,1,26.5-15.8l1-10.427a140.264,140.264,0,0,1-22-10.026l.5,12.369c2.707,1.272,5.261,2.389,7.589,3.355-7.13,3.844-16.708,10.18-28.044,20.533,0,0,.139,1.107,2.251,10.936a69.511,69.511,0,0,1,29.387,2.128,47.359,47.359,0,0,0-9.476,4.077l.59,12.272c9.737-6.936,22.854-9.747,22.854-9.747l.985-8.752h0Z'
          fill={color}
        />
        <path
          d='M1297.648,597.114l4.795-2.757c1.5,4.615,5.094,7.612,11.208,7.612,5.933,0,8.87-2.7,8.87-6.473,0-4.315-3.716-5.753-9.888-7.672-6.713-2.157-13.185-4.255-13.185-12.046,0-7.551,6.173-11.866,13.185-11.866a15.079,15.079,0,0,1,14.2,9.169l-4.675,2.7a9.764,9.764,0,0,0-9.529-6.472c-4.5,0-7.612,2.4-7.612,6.233,0,3.9,2.937,5.334,8.81,7.192,7.311,2.4,14.2,4.555,14.2,12.646,0,7.372-5.933,12.046-14.5,12.046C1305.559,607.422,1299.865,603.407,1297.648,597.114Z'
          transform='translate(-258.635 -7.56)'
          fill={color}
        />
        <path
          d='M1396.317,578.839a13.258,13.258,0,0,1-13.544,13.484h-9.649v14.983h-5.573V565.354h15.223A13.222,13.222,0,0,1,1396.317,578.839Zm-5.514,0a7.907,7.907,0,0,0-8.031-8.271h-9.649V587.11h9.649A7.941,7.941,0,0,0,1390.8,578.839Z'
          transform='translate(-290.777 -8.223)'
          fill={color}
        />
        <path
          d='M1430.351,585.667a21.725,21.725,0,1,1,21.7,21.755A21.551,21.551,0,0,1,1430.351,585.667Zm37.877,0a16.182,16.182,0,1,0-16.182,16.3A16.007,16.007,0,0,0,1468.228,585.667Z'
          transform='translate(-319.655 -7.56)'
          fill={color}
        />
        <path
          d='M1539.677,591.245h-9.889v16.062h-5.574V565.354H1541a12.946,12.946,0,0,1,4.375,25.172l9.889,16.781h-6.173Zm-9.889-5.034H1541a7.828,7.828,0,0,0,0-15.642h-11.208Z'
          transform='translate(-362.816 -8.223)'
          fill={color}
        />
        <path
          d='M1616.282,570.628h-12.406v36.678h-5.514V570.628h-12.346v-5.274h30.266Z'
          transform='translate(-391.234 -8.223)'
          fill={color}
        />
        <path
          d='M1647.708,597.114l4.795-2.757c1.5,4.615,5.094,7.612,11.208,7.612,5.933,0,8.87-2.7,8.87-6.473,0-4.315-3.716-5.753-9.888-7.672-6.713-2.157-13.185-4.255-13.185-12.046,0-7.551,6.173-11.866,13.185-11.866a15.079,15.079,0,0,1,14.2,9.169l-4.675,2.7a9.764,9.764,0,0,0-9.529-6.472c-4.5,0-7.612,2.4-7.612,6.233,0,3.9,2.936,5.334,8.81,7.192,7.311,2.4,14.2,4.555,14.2,12.646,0,7.372-5.933,12.046-14.5,12.046C1655.619,607.422,1649.925,603.407,1647.708,597.114Z'
          transform='translate(-419.601 -7.56)'
          fill={color}
        />
      </g>
    </svg>
  )
}

export default CanlanTextLogo
